// Persists in-progress operation ids (translation, duplication) to localStorage so
// the "Translating…" / "Duplicating…" overlay survives a page refresh. The socket
// completion events still drive the live "operation finished" removal — this is
// only the durable layer that the Collections page reads on mount.
//
// Entries auto-expire after 24h: real translations/clones finish well before then,
// so any leftover after that is a stuck/lost state we don't want to render forever.

const STORAGE_KEY = 'mcg_in_progress_v1'
const TTL_MS = 24 * 60 * 60 * 1000

const safeRead = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

const safeWrite = data => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  } catch {
    // quota / disabled storage — ignore
  }
}

const pruneExpired = entries => {
  if (!Array.isArray(entries)) return []
  const cutoff = Date.now() - TTL_MS
  return entries.filter(e => e && typeof e.id === 'string' && typeof e.ts === 'number' && e.ts >= cutoff)
}

export const addInProgress = (operation, id) => {
  if (!operation || !id) return
  const data = safeRead()
  const existing = pruneExpired(data[operation])
  if (!existing.some(e => e.id === id)) {
    existing.push({ id, ts: Date.now() })
  }
  data[operation] = existing
  safeWrite(data)
}

export const removeInProgress = (operation, id) => {
  if (!operation || !id) return
  const data = safeRead()
  const existing = pruneExpired(data[operation])
  data[operation] = existing.filter(e => e.id !== id)
  safeWrite(data)
}

export const getInProgress = operation => {
  if (!operation) return []
  const data = safeRead()
  const existing = pruneExpired(data[operation])
  // Re-write so expired entries get cleaned up over time.
  if (existing.length !== (data[operation]?.length ?? 0)) {
    data[operation] = existing
    safeWrite(data)
  }
  return existing.map(e => e.id)
}

export const IN_PROGRESS_OPERATIONS = {
  TRANSLATION: 'translation',
  DUPLICATION: 'duplication',
}
